import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, KeyRound, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { claudeCodeConfig, kindLabel, lastUsedLabel, type ClaudeConnectionRow } from "./claudeConnectionText";

const KEY = ["/api/claude-connections"];

export function ClaudeConnection() {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError } = useQuery<ClaudeConnectionRow[]>({
    queryKey: KEY,
    staleTime: 0,
    queryFn: async () => (await apiRequest("GET", "/api/claude-connections")).json(),
  });
  const rows = Array.isArray(data) ? data : [];

  const create = useMutation({
    mutationFn: async () =>
      (await apiRequest("POST", "/api/claude-connections", { label })).json() as Promise<{ key: string }>,
    onSuccess: ({ key }) => {
      setNewKey(key); // held in memory only, shown once
      setLabel("");
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/claude-connections/${id}`),
    onSuccess: () => {
      toast.success("Connection revoked");
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      toast.error("Couldn't copy. Select the key and copy it by hand.");
      return;
    }
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <KeyRound className="w-4 h-4 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">Claude connection</p>
          <p className="text-xs text-muted-foreground">
            Cowork and claude.ai connect through the FTC CRM connector and sign in as you. Claude Code needs a personal key. Disconnecting in Claude doesn't end access; revoke it here.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Name this key, e.g. Claude Code, MacBook"
          className="h-8 text-xs"
          maxLength={80}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs shrink-0"
          onClick={() => create.mutate()}
          disabled={!label.trim() || create.isPending}
        >
          {create.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Create personal key"}
        </Button>
      </div>

      {isLoading ? null : isError ? (
        <p className="text-xs text-destructive">Couldn't load your Claude connections. Refresh the page to try again.</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No Claude connections yet.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm truncate">{r.label}</p>
                <p className="text-xs text-muted-foreground">
                  <code className="font-mono">{r.prefix}…</code> · {lastUsedLabel(r.lastUsedAt)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-[11px]">{kindLabel(r.kind)}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => revoke.mutate(r.id)}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!newKey} onOpenChange={(open) => !open && setNewKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your personal key</DialogTitle>
            <DialogDescription>
              Copy it now. It won't be shown again. If you lose it, revoke it and make a new one.
            </DialogDescription>
          </DialogHeader>
          {newKey && (
            <div className="space-y-3">
              <code className="block break-all rounded bg-muted p-2 text-xs font-mono">{newKey}</code>
              <p className="text-xs text-muted-foreground">For Claude Code, this goes in ~/.claude.json under mcpServers:</p>
              <pre className="overflow-x-auto rounded bg-muted p-2 text-[11px] font-mono">{claudeCodeConfig(newKey)}</pre>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => copy(newKey)}>
                  {copied ? <Check className="w-3 h-3" /> : "Copy key"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => copy(claudeCodeConfig(newKey))}>
                  Copy config
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
